/**
 * Gameplay tuning. Everything the deterministic simulation reads lives here so a
 * port (Expo / native / server-side replay verification) shares one source of truth.
 *
 * Units: metres, seconds, m/s, m/s².
 */
export interface GameTuning {
  /** Fixed simulation rate. Never tie physics to render rate. */
  tickRate: number;
  /** Steering is quantised to this many steps per side so replays are exact. */
  steerSteps: number;

  gravity: number;
  /** Gravity multiplier while holding (diving / "heavy"). */
  holdGravityMul: number;
  /** Gravity multiplier while airborne and not holding. */
  airGravityMul: number;
  /** Extra push along the slope while holding on a downslope, as a fraction of gravity. */
  diveSlopeBonus: number;

  /** The pearl always rolls at least this fast on the ground (rises with progress). */
  runSpeedStart: number;
  runSpeedEnd: number;
  runSpeedRampDistance: number;
  runAccel: number;

  maxSpeed: number;
  /** Linear rolling resistance (1/s): decel = groundFriction · v */
  groundFriction: number;
  /** Quadratic air drag (1/m): decel = airDrag · v² */
  airDrag: number;

  /** Steering = sideways (centripetal) acceleration: base + perSpeed·v, capped. */
  turnAccelBase: number;
  turnAccelPerSpeed: number;
  turnAccelMax: number;
  /** Fraction of steering authority available in the air. */
  airTurn: number;
  /** Launches are capped to this vertical speed; the excess is kept as forward speed (distance over height). */
  maxLaunchVy: number;

  /** Landing quality thresholds (degrees between velocity and ground tangent). */
  perfectAngleDeg: number;
  goodAngleDeg: number;
  perfectBoost: number;
  perfectBoostPerCombo: number;
  perfectBoostMax: number;
  /** Minimum airtime (s) for a landing to count as a landing event at all. */
  minAirTime: number;

  playerRadius: number;
  playerHeight: number;
  closeCallMargin: number;
  closeCallBoost: number;

  startSpeed: number;
}

export const TUNING: GameTuning = {
  tickRate: 120,
  steerSteps: 32,

  gravity: 30,
  holdGravityMul: 2.6,
  airGravityMul: 0.92,
  diveSlopeBonus: 0.4,

  runSpeedStart: 18,
  runSpeedEnd: 26,
  runSpeedRampDistance: 4000,
  runAccel: 10,

  maxSpeed: 95,
  groundFriction: 0.018,
  airDrag: 0.0005,

  turnAccelBase: 14,
  turnAccelPerSpeed: 0.42,
  turnAccelMax: 40,
  airTurn: 0.45,
  maxLaunchVy: 13,

  perfectAngleDeg: 20,
  goodAngleDeg: 34,
  perfectBoost: 0.09,
  perfectBoostPerCombo: 0.015,
  perfectBoostMax: 0.16,
  minAirTime: 0.28,

  playerRadius: 0.62,
  playerHeight: 1.24,
  closeCallMargin: 1.6,
  closeCallBoost: 0.025,

  startSpeed: 14,
};
